import { db } from '../config/firebasedeconfig';
import { collection, addDoc, query, where, getDocs, updateDoc, doc, serverTimestamp, orderBy, deleteDoc, getDoc } from 'firebase/firestore';

/**
 * Service de gestion des notifications
 */
const notificationService = {
    /**
     * Crée une nouvelle notification
     * @param {string} userId - ID de l'utilisateur destinataire
     * @param {Object} notificationData - Données de la notification
     * @returns {Promise<string>} - ID de la notification créée
     */
    createNotification: async (userId, notificationData) => {
        try {
            const notificationsRef = collection(db, 'users', userId, 'notifications');

            // Ajouter la notification avec les champs par défaut
            const docRef = await addDoc(notificationsRef, {
                ...notificationData,
                read: false,
                createdAt: serverTimestamp()
            });

            return docRef.id;
        } catch (error) {
            console.error('Erreur lors de la création de la notification:', error);
            throw error;
        }
    },

    /**
     * Récupère les notifications non lues d'un utilisateur
     * @param {string} userId - ID de l'utilisateur
     * @returns {Promise<Array>} - Tableau des notifications non lues
     */
    getUnreadNotifications: async (userId) => {
        try {
            const notificationsRef = collection(db, 'users', userId, 'notifications');
            const q = query(
                notificationsRef,
                where('read', '==', false),
                orderBy('createdAt', 'desc')
            );

            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error('Erreur lors de la récupération des notifications non lues:', error);
            throw error;
        }
    },

    /**
     * Marque une notification comme lue
     * @param {string} userId - ID de l'utilisateur
     * @param {string} notificationId - ID de la notification
     * @returns {Promise<void>}
     */
    markAsRead: async (userId, notificationId) => {
        try {
            const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
            await updateDoc(notificationRef, {
                read: true
            });
        } catch (error) {
            console.error('Erreur lors du marquage de la notification comme lue:', error);
            throw error;
        }
    },

    /**
     * Marque toutes les notifications d'un utilisateur comme lues
     * @param {string} userId - ID de l'utilisateur
     * @returns {Promise<void>}
     */
    markAllAsRead: async (userId) => {
        try {
            const notificationsRef = collection(db, 'users', userId, 'notifications');
            const q = query(notificationsRef, where('read', '==', false));
            const querySnapshot = await getDocs(q);

            const batch = [];
            querySnapshot.forEach(docSnapshot => {
                const notificationRef = doc(db, 'users', userId, 'notifications', docSnapshot.id);
                batch.push(updateDoc(notificationRef, { read: true }));
            });

            await Promise.all(batch);
        } catch (error) {
            console.error('Erreur lors du marquage de toutes les notifications comme lues:', error);
            throw error;
        }
    },

    /**
     * Supprime une notification
     * @param {string} userId - ID de l'utilisateur
     * @param {string} notificationId - ID de la notification
     * @returns {Promise<void>}
     */
    deleteNotification: async (userId, notificationId) => {
        try {
            const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
            await deleteDoc(notificationRef);
        } catch (error) {
            console.error('Erreur lors de la suppression de la notification:', error);
            throw error;
        }
    },

    /**
     * Récupère le nombre de notifications non lues
     * @param {string} userId - ID de l'utilisateur
     * @returns {Promise<number>} - Nombre de notifications non lues
     */
    getUnreadCount: async (userId) => {
        try {
            const notificationsRef = collection(db, 'users', userId, 'notifications');
            const q = query(notificationsRef, where('read', '==', false));
            const querySnapshot = await getDocs(q);
            return querySnapshot.size;
        } catch (error) {
            console.error('Erreur lors de la récupération du nombre de notifications non lues:', error);
            return 0;
        }
    },

    /**
     * Crée une notification pour un message non lu
     * @param {string} recipientId - ID du destinataire
     * @param {string} senderId - ID de l'expéditeur
     * @param {string} senderName - Nom de l'expéditeur
     * @returns {Promise<string>} - ID de la notification créée
     */
    createMessageNotification: async (recipientId, senderId, senderName) => {
        try {
            const message = `<strong>${senderName}</strong> vous a écrit. Lisez son message depuis votre messagerie.`;

            return await notificationService.createNotification(recipientId, {
                type: 'message',
                message,
                fromUserId: senderId,
                fromUsername: senderName,
                read: false
            });
        } catch (error) {
            console.error('Erreur lors de la création de notification de message:', error);
            throw error;
        }
    },

    /**
     * Crée une notification pour un appel manqué
     * @param {string} recipientId - ID du destinataire
     * @param {string} callerId - ID de l'appelant
     * @param {string} callerName - Nom de l'appelant
     * @param {string} callId - ID de l'appel manqué
     * @returns {Promise<string>} - ID de la notification créée
     */
    createMissedCallNotification: async (recipientId, callerId, callerName, callId = null) => {
        try {
            const message = `<strong>${callerName}</strong> a tenté de vous rejoindre par appel vidéo.`;

            return await notificationService.createNotification(recipientId, {
                type: 'call',
                message,
                fromUserId: callerId,
                fromUsername: callerName,
                callId: callId,
                read: false
            });
        } catch (error) {
            console.error('Erreur lors de la création de notification d\'appel:', error);
            throw error;
        }
    },

    /**
     * Crée une notification pour un sujet devenu "en feu"
     * @param {string} authorId - ID de l'auteur du sujet
     * @param {string} authorName - Nom de l'auteur
     * @param {string} subjectId - ID du sujet
     * @param {string} subjectTitle - Titre du sujet
     * @returns {Promise<string>} - ID de la notification créée
     */
    createHotTopicNotification: async (authorId, authorName, subjectId, subjectTitle) => {
        try {
            const message = `Félicitation <strong>${authorName}</strong> ! Votre sujet <strong>${subjectTitle}</strong> est devenu en feu.`;

            return await notificationService.createNotification(authorId, {
                type: 'forum_hot',
                message,
                subjectId,
                subjectTitle,
                read: false
            });
        } catch (error) {
            console.error('Erreur lors de la création de notification de sujet en feu:', error);
            throw error;
        }
    },

    /**
     * Crée une notification pour un sujet déclassé
     * @param {string} authorId - ID de l'auteur du sujet
     * @param {string} authorName - Nom de l'auteur
     * @param {string} subjectId - ID du sujet
     * @param {string} subjectTitle - Titre du sujet
     * @returns {Promise<string>} - ID de la notification créée
     */
    createColdTopicNotification: async (authorId, authorName, subjectId, subjectTitle) => {
        try {
            const message = `Damnation <strong>${authorName}</strong> ! Votre sujet <strong>${subjectTitle}</strong> est maintenant déclassé.`;

            return await notificationService.createNotification(authorId, {
                type: 'forum_cold',
                message,
                subjectId,
                subjectTitle,
                read: false
            });
        } catch (error) {
            console.error('Erreur lors de la création de notification de sujet déclassé:', error);
            throw error;
        }
    },

    /**
     * Crée une notification pour une réponse à un sujet
     * @param {string} authorId - ID de l'auteur du sujet
     * @param {string} responderId - ID de celui qui répond
     * @param {string} responderName - Nom de celui qui répond
     * @param {string} subjectId - ID du sujet
     * @param {string} subjectTitle - Titre du sujet
     * @returns {Promise<string>} - ID de la notification créée
     */
    createTopicReplyNotification: async (authorId, responderId, responderName, subjectId, subjectTitle) => {
        try {
            const message = `<strong>${responderName}</strong> a répondu à votre sujet <strong>${subjectTitle}</strong>.`;

            return await notificationService.createNotification(authorId, {
                type: 'forum_reply',
                message,
                fromUserId: responderId,
                fromUsername: responderName,
                subjectId,
                subjectTitle,
                read: false
            });
        } catch (error) {
            console.error('Erreur lors de la création de notification de réponse:', error);
            throw error;
        }
    },

    /**
     * Crée une notification pour un like sur une réponse
     * @param {string} authorId - ID de l'auteur de la réponse
     * @param {string} likerId - ID de celui qui a liké
     * @param {string} likerName - Nom de celui qui a liké
     * @param {string} subjectId - ID du sujet
     * @param {string} subjectTitle - Titre du sujet
     * @returns {Promise<string>} - ID de la notification créée
     */
    createReplyLikeNotification: async (
        authorId,
        likerId,
        likerName,
        subjectId,
        subjectTitle,
        replyId = null
    ) => {
        try {
            const message = `<strong>${likerName}</strong> a aimé votre réponse dans le topic <strong>${subjectTitle}</strong>.`;

            return await notificationService.createNotification(authorId, {
                type: 'forum_like',
                message,
                fromUserId: likerId,
                fromUsername: likerName,
                subjectId,
                subjectTitle,
                replyId: replyId || null,
                read: false
            });
        } catch (error) {
            console.error('Erreur lors de la création de notification de like:', error);
            throw error;
        }
    },

    /**
     * Crée une notification pour la période zodiacale
     * @param {string} userId - ID de l'utilisateur
     * @param {string} username - Nom de l'utilisateur
     * @param {string} zodiacSign - Signe astrologique
     * @param {Date} startDate - Date de début
     * @param {Date} endDate - Date de fin
     * @returns {Promise<string>} - ID de la notification créée
     */
    createZodiacPeriodNotification: async (userId, username, zodiacSign, startDate, endDate) => {
        try {
            const formattedStartDate = startDate.toLocaleDateString('fr-FR');
            const formattedEndDate = endDate.toLocaleDateString('fr-FR');

            const message = `<strong>${username}</strong>, les astres vous sourient ! Votre ère ${zodiacSign} brille de mille feux du ${formattedStartDate} au ${formattedEndDate}. C'est le moment parfait pour concrétiser vos projets et vivre pleinement votre passion du cinéma sous l'influence de votre signe.`;

            // Extraire le signe zodiacal sans le "du" ou "de la" pour le stocker comme identifiant
            let rawZodiacSign = '';
            if (zodiacSign.startsWith('du ')) {
                rawZodiacSign = zodiacSign.substring(3).toLowerCase();
            } else if (zodiacSign.startsWith('de la ')) {
                rawZodiacSign = zodiacSign.substring(6).toLowerCase();
            } else if (zodiacSign.startsWith('des ')) {
                rawZodiacSign = zodiacSign.substring(4).toLowerCase();
            } else {
                rawZodiacSign = zodiacSign.toLowerCase();
            }

            // Correspondance des noms français vers les clés anglaises
            const zodiacMapping = {
                'bélier': 'aries',
                'taureau': 'taurus',
                'gémeaux': 'gemini',
                'cancer': 'cancer',
                'lion': 'leo',
                'vierge': 'virgo',
                'balance': 'libra',
                'scorpion': 'scorpio',
                'sagittaire': 'sagittarius',
                'capricorne': 'capricorn',
                'verseau': 'aquarius',
                'poissons': 'pisces'
            };

            // Utiliser la correspondance ou conserver la valeur d'origine
            const zodiacKey = zodiacMapping[rawZodiacSign] || rawZodiacSign;

            return await notificationService.createNotification(userId, {
                type: 'zodiac',
                message,
                zodiacSign: zodiacKey,
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                read: false
            });
        } catch (error) {
            console.error('Erreur lors de la création de notification zodiacale:', error);
            throw error;
        }
    },

    /**
     * Crée une notification pour une fête spéciale (Noël, Halloween, etc.)
     * @param {string} userId - ID de l'utilisateur
     * @param {string} username - Nom de l'utilisateur
     * @param {string} holidayName - Nom de la fête
     * @returns {Promise<string>} - ID de la notification créée
     */
    createHolidayNotification: async (userId, username, holidayName) => {
        try {
            let message = '';

            // Messages personnalisés selon la fête
            switch (holidayName) {
                case "le Noël":
                    message = `<strong>${username}</strong>, joyeux Noël ! Que cette période festive vous apporte chaleur et magie. Profitez-en pour découvrir notre sélection de films de Noël.`;
                    break;
                case "l'Halloween":
                    message = `<strong>${username}</strong>, bon Halloween ! L'heure est venue de frissonner devant nos films d'horreur sélectionnés spécialement pour vous.`;
                    break;
                case "la Saint-Valentin":
                    message = `<strong>${username}</strong>, bonne Saint-Valentin ! Laissez-vous séduire par notre collection de films romantiques pour cette journée spéciale.`;
                    break;
                case "la Pâques":
                    message = `<strong>${username}</strong>, joyeuses Pâques ! Profitez de cette période pour partager des films en famille et découvrir notre sélection pascale.`;
                    break;
                case "le Nouvel An":
                    message = `<strong>${username}</strong>, bonne année ! Que cette nouvelle année soit remplie de films extraordinaires et d'aventures cinématographiques.`;
                    break;
                case "la Fête du Canada":
                    message = `<strong>${username}</strong>, bonne fête du Canada ! Célébrez avec notre sélection de films canadiens qui font notre fierté nationale.`;
                    break;
                case "la Fête Nationale (France)":
                    message = `<strong>${username}</strong>, bonne fête nationale ! Plongez dans l'histoire et la culture française avec notre collection de films tricolores.`;
                    break;
                case "la Fête Nationale (États-Unis)":
                    message = `<strong>${username}</strong>, happy Independence Day ! Découvrez notre sélection de films emblématiques célébrant la liberté et l'identité américaine.`;
                    break;
                case "la Saint-Jean-Baptiste":
                    message = `<strong>${username}</strong>, bonne Saint-Jean-Baptiste ! Explorez notre collection de films québécois pour célébrer cette fête de la culture franco-canadienne.`;
                    break;
                default:
                    message = `<strong>${username}</strong>, c'est la fête ! Découvrez notre sélection spéciale de films pour cette période de célébration.`;
            }

            return await notificationService.createNotification(userId, {
                type: 'holiday',
                message,
                holidayName,
                read: false
            });
        } catch (error) {
            console.error('Erreur lors de la création de notification de fête:', error);
            throw error;
        }
    },


    /**
     * Crée une notification quand l'utilisateur atteint un nouveau titre-niveau XP.
     * @param {string} recipientId - ID de l'utilisateur concerné
     * @param {string} levelKey - Clé du titre atteint
     * @param {number} levelNumber - Numéro du niveau atteint
     * @returns {Promise<string>} - ID de la notification créée
     */
    createLevelUpNotification: async (recipientId, levelKey, levelNumber) => {
        try {
            const message = `Félicitations ! Vous avez atteint un nouveau titre MovieFrenzy.`;

            return await notificationService.createNotification(recipientId, {
                type: 'level_up',
                message,
                fromUserId: recipientId,
                profileUserId: recipientId,
                levelKey,
                levelNumber,
                read: false
            });
        } catch (error) {
            console.error('Erreur lors de la création de notification level_up:', error);
            throw error;
        }
    },

    /**
     * Vérifie s'il existe déjà une notification zodiacale active pour un utilisateur et un signe
     * @param {string} userId - ID de l'utilisateur
     * @param {string} zodiacSign - Signe zodiacal
     * @returns {Promise<boolean>} - True si une notification existe déjà
     */
    hasActiveZodiacNotification: async (userId, zodiacSign) => {
        try {
            const notificationsRef = collection(db, 'users', userId, 'notifications');
            const q = query(
                notificationsRef,
                where('type', '==', 'zodiac'),
                where('zodiacSign', '==', zodiacSign)
            );

            const querySnapshot = await getDocs(q);
            return !querySnapshot.empty;
        } catch (error) {
            console.error('Erreur lors de la vérification des notifications zodiacales actives:', error);
            return false;
        }
    },

    /**
     * Vérifie s'il existe déjà une notification de fête active pour un utilisateur et une fête
     * @param {string} userId - ID de l'utilisateur
     * @param {string} holidayName - Nom de la fête
     * @returns {Promise<boolean>} - True si une notification existe déjà
     */
    hasActiveHolidayNotification: async (userId, holidayName) => {
        try {
            const notificationsRef = collection(db, 'users', userId, 'notifications');
            const q = query(
                notificationsRef,
                where('type', '==', 'holiday'),
                where('holidayName', '==', holidayName)
            );

            const querySnapshot = await getDocs(q);
            return !querySnapshot.empty;
        } catch (error) {
            console.error('Erreur lors de la vérification des notifications de fête actives:', error);
            return false;
        }
    }
};

export default notificationService; 